import axios from 'axios';
import Cookies from 'js-cookie';

import config from '../../config';
import { environment } from './environment';
import getMinutes from '../utilities/getMinutes';

const {
  authenticate,
  cookieDomain,
  auth0,
  alm,
  gs,
  drxMain,
} = config;

const tokenUrl = authenticate[environment].token;
const authUrl = authenticate[environment].authorize;
const revokeUrl = authenticate[environment].revoke;
const domainValue = cookieDomain[environment];
const auth0ClientId = auth0[environment].clientId;
const auth0LogoutRedirectUrl = auth0[environment].logoutRedirectUrl;
const auth0CustomDomain = auth0[environment].customDomain;

const LOGOUT_DELAY_MS = 3000;

/**
 * Utility: Wait for a given delay
 */
function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Utility: Redirect after optional delay
 */
async function redirectTo(url, delay = LOGOUT_DELAY_MS) {
  if (typeof window === 'undefined') {
    console.warn(
      '[redirect] Skipping redirect (window is undefined). Target:',
      url,
    );
    return;
  }
  console.log(
    `[redirect] Waiting ${delay}ms before redirecting to:`,
    url,
  );
  await wait(delay);
  window.location.replace(url);
}

/**
 * Utility: Clear all authentication cookies
 */
function clearLoggedInUser() {
  console.log('[clearLoggedInUser] Clearing user cookies...');
  const cookiesToRemove = [
    'currentUser',
    'accessToken',
    'appSession.0',
    'appSession.1',
    'currentUserAttributes',
  ];
  cookiesToRemove.forEach((cookie) =>
    Cookies.remove(cookie, { domain: domainValue }),
  );
}

/**
 * Utility: Trigger logout from all provider endpoints
 */
function logoutFromProviders(logoutUrls) {
  if (typeof window === 'undefined' || !window.document) {
    console.warn(
      '[logoutFromProviders] Skipping provider logout (window/document undefined)',
    );
    return;
  }

  console.log('[logoutFromProviders] Logging out from providers...');
  logoutUrls.forEach((url) => {
    console.log('[logoutFromProviders] Logging out from URL:', url);
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    document.body.appendChild(iframe);
  });
}

/**
 * Utility: Fire ALM + GS logout, then continue to the final destination.
 * Called from LogoutCallbackHandler, once Auth0's own logout has already
 * completed via a real top-level redirect (see `logout` below for why).
 */
function logoutFromRemainingProviders(country = 'us', lng = 'en') {
  const logoutUrls = [
    `${alm[environment].host}/api/auth/logout`,
    `${gs[environment].host}/${country}/${lng}/${gs[environment].logoutPath}?origin=${drxMain[environment]}`,
  ];

  logoutFromProviders(logoutUrls);
}

/**
 * Main: Logout
 *
 * Auth0's custom domain sends X-Frame-Options: sameorigin, so its /v2/logout
 * endpoint refuses to load in the hidden iframe logoutFromProviders() uses —
 * the browser blocks it outright and Auth0's session survives. Auth0 logout
 * has to be a real top-level navigation instead. Auth0's own
 * logoutRedirectUrl brings the user back here to /callback
 * (LogoutCallbackHandler), which fires ALM/GS logout (still iframed — only
 * Auth0 is confirmed to block framing) and completes the redirect to
 * referrerUrl/home from there.
 */
async function logout() {
  console.log('[logout] Initiating logout process...');
  const token = authenticationService.currentUserValue;
  console.log('[logout] Current user token:', token);

  if (token) {
    try {
      console.log('[logout] Revoking token...');
      const response = await axios.post(revokeUrl, { token });
      console.log(
        '[logout] Token revoked successfully:',
        response.data,
      );
    } catch (error) {
      console.error('[logout] Error revoking token:', error);
    }
  } else {
    console.warn('[logout] No token found.');
  }

  clearLoggedInUser();
  const auth0LogoutUrl = `${auth0CustomDomain}/v2/logout?client_id=${auth0ClientId}&returnTo=${encodeURIComponent(auth0LogoutRedirectUrl)}`;
  await redirectTo(auth0LogoutUrl);
}

/**
 * Main: Renew
 */
function renew() {
  console.log('[renew] Renewing user session...');
  clearLoggedInUser();
  if (typeof window !== 'undefined') {
    console.log('[renew] Redirecting to authorization URL:', authUrl);
    window.location.replace(authUrl);
  } else {
    console.warn(
      '[renew] Skipping redirect (window is undefined). Target:',
      authUrl,
    );
  }
  return null;
}

/**
 * Utility: One-way fingerprint for correlating log lines about the same auth
 * code across mounts/retries without logging the code itself (it's a
 * bearer-equivalent secret during its short validity window).
 */
function fingerprintCode(code) {
  if (!code) return null;
  let hash = 0;
  for (let i = 0; i < code.length; i += 1) {
    hash = (hash * 31 + code.charCodeAt(i)) | 0;
  }
  return hash.toString(16);
}

/**
 * Main: Login
 */
async function login(code) {
  const codeFingerprint = fingerprintCode(code);
  console.log('[login] Logging in (code fingerprint):', codeFingerprint);
  try {
    const response = await axios.post(tokenUrl, { code });
    console.log('[login] Login response:', response.status);

    if (response.status !== 200) {
      if ([401, 403].includes(response.status)) {
        console.warn(
          '[login] Unauthorized or forbidden. Logging out...',
        );
        await logout();
      }
      throw new Error(response.message);
    }

    const { idToken, attributes, app } = response.data;
    console.log('[login] User data received:', { idToken, app });

    if (app) {
      console.log('[login] Storing current user token...');
      Cookies.set('currentUser', idToken, {
        domain: domainValue,
        secure: true,
        expires: getMinutes(60),
      });
    }

    console.log('[login] Storing user attributes...');
    Cookies.set('currentUserAttributes', attributes, {
      domain: domainValue,
      secure: true,
      expires: getMinutes(60),
    });

    return { idToken, app };
  } catch (error) {
    // error.response.data carries the real backend/Auth0 error (e.g.
    // invalid_grant) that a bare console.error(error) tends to bury inside
    // the axios error object rather than surface directly.
    console.error('[login] Error logging in — code fingerprint:', codeFingerprint, {
      status: error.response && error.response.status,
      data: error.response && error.response.data,
      message: error.message,
    });
    throw error;
  }
}

/**
 * Public API
 */
const authenticationService = {
  login,
  logout,
  logoutFromRemainingProviders,
  renew,
  updateCurrentUser(user) {
    console.log('[updateCurrentUser] Updating current user:', user);
    Cookies.set('currentUser', user, {
      domain: domainValue,
      secure: true,
      expires: getMinutes(60),
    });
  },
  get currentUserValue() {
    const currentUser = Cookies.get('currentUser');
    console.log(
      '[currentUserValue] Current user value:',
      currentUser,
    );
    return currentUser;
  },
};

export default authenticationService;
export { authenticationService };
